<template>
  <div class="home">
    <!-- ============ 1. 顶部轮播图 ============ -->
    <el-carousel v-if="banners.length" class="banner" height="440px" :interval="5000">
      <el-carousel-item v-for="item in banners" :key="item.id">
        <div class="banner-item" :class="{ clickable: !!item.link }" @click="goLink(item.link)">
          <img class="banner-img" :src="item.image" :alt="item.title">
          <!-- 图片标题浮层（后台没填标题时不显示） -->
          <div v-if="item.title" class="banner-caption">
            <div class="container">
              <h2>{{ item.title }}</h2>
            </div>
          </div>
        </div>
      </el-carousel-item>
    </el-carousel>
    <!-- 后台还没配置轮播图时，给一个占位提示 -->
    <div v-else class="banner banner-empty">暂无轮播图，请在后台管理系统中添加</div>

    <!-- ============ 2. 核心产品 ============ -->
    <section class="section">
      <div class="container">
        <div class="section-head">
          <h3 class="section-title">核心产品</h3>
          <router-link to="/products" class="section-more">查看更多 <i class="el-icon-arrow-right"></i></router-link>
        </div>

        <div v-loading="loading" class="product-grid">
          <router-link v-for="p in products" :key="p.id" :to="`/products/${p.id}`" class="product-card">
            <div class="product-cover">
              <img v-if="p.cover" :src="p.cover" :alt="p.name">
              <div v-else class="img-placeholder">暂无图片</div>
            </div>
            <div class="product-body">
              <h4 class="product-name">{{ p.name }}</h4>
              <p class="product-summary">{{ p.summary || '暂无简介' }}</p>
              <div class="product-price">{{ priceText(p.price) }}</div>
            </div>
          </router-link>
        </div>
        <el-empty v-if="!loading && products.length === 0" description="暂无产品" />
      </div>
    </section>

    <!-- ============ 3. 新闻动态 ============ -->
    <section class="section section-gray">
      <div class="container">
        <div class="section-head">
          <h3 class="section-title">新闻动态</h3>
          <router-link to="/news" class="section-more">查看更多 <i class="el-icon-arrow-right"></i></router-link>
        </div>

        <div v-loading="loading" class="news-list">
          <router-link v-for="n in news" :key="n.id" :to="`/news/${n.id}`" class="news-item">
            <div class="news-cover">
              <img v-if="n.cover" :src="n.cover" :alt="n.title">
              <div v-else class="img-placeholder">暂无图片</div>
            </div>
            <div class="news-body">
              <h4 class="news-title">{{ n.title }}</h4>
              <p class="news-summary">{{ n.summary || '暂无摘要' }}</p>
              <div class="news-meta"><i class="el-icon-date"></i> {{ dateText(n.created_at) }}</div>
            </div>
          </router-link>
        </div>
        <el-empty v-if="!loading && news.length === 0" description="暂无新闻" />
      </div>
    </section>
  </div>
</template>

<script>
import { getBanners, getProductList, getNewsList } from '../api'

export default {
  name: 'Home',
  data() {
    return {
      loading: false,   // 数据加载中
      banners: [],      // 轮播图
      products: [],     // 首页展示的产品（只取前 6 个）
      news: []          // 首页展示的新闻（只取前 6 条）
    }
  },
  created() {
    this.fetchAll()
  },
  methods: {
    // 三个接口互不依赖，用 Promise.all 并行请求，页面更快
    async fetchAll() {
      this.loading = true
      try {
        const [bannerRes, productRes, newsRes] = await Promise.all([
          getBanners(),
          getProductList({ page: 1, size: 6 }),
          getNewsList({ page: 1, size: 6 })
        ])
        this.banners = bannerRes.data.list
        this.products = productRes.data.list
        this.news = newsRes.data.list
      } finally {
        this.loading = false
      }
    },
    // 点击轮播图跳转（后台填了链接才跳，新窗口打开）
    goLink(link) {
      if (link) window.open(link, '_blank')
    },
    // 价格显示：没填价格时显示「面议」
    priceText(price) {
      if (price === null || price === undefined) return '面议'
      return '￥' + Number(price).toFixed(2)
    },
    // 时间只取日期部分（年-月-日）
    dateText(datetime) {
      return (datetime || '').slice(0, 10)
    }
  }
}
</script>

<style scoped>
/* ---------- 轮播图 ---------- */
.banner { background: #000; }
.banner-empty {
  height: 440px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  background: #eef0f3;
  font-size: 15px;
}
.banner-item {
  position: relative;
  width: 100%;
  height: 100%;
}
.banner-item.clickable { cursor: pointer; }
.banner-img {
  width: 100%;
  height: 100%;
  object-fit: cover;   /* 图片按比例铺满，不拉伸变形 */
}
.banner-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 60px 0 32px;
  /* 从下往上的黑色渐变，保证文字在任何图片上都看得清 */
  background: linear-gradient(to top, rgba(0, 0, 0, 0.65), transparent);
  color: #fff;
}
.banner-caption h2 {
  margin: 0;
  font-size: 32px;
  font-weight: 600;
}

/* ---------- 通用区块 ---------- */
.section { padding: 60px 0; }
.section-gray { background: #ffffff; }

/* ---------- 产品网格（自适应：宽屏 3 列，窄屏自动换行） ---------- */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  min-height: 80px;
}
.product-card {
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.25s;
}
.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}
.product-cover { height: 190px; background: #f5f7fa; }
.product-cover img { width: 100%; height: 100%; object-fit: cover; }
.product-body { padding: 16px 18px 20px; }
.product-name {
  margin: 0 0 8px;
  font-size: 17px;
  color: #1f2d3d;
  /* 标题过长时省略号 */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.product-summary {
  margin: 0 0 12px;
  height: 42px;
  font-size: 13px;
  color: #909399;
  line-height: 1.6;
  overflow: hidden;
}
.product-price { color: #f56c6c; font-size: 18px; font-weight: 600; }

/* ---------- 新闻列表（左侧封面 + 右侧文字） ---------- */
.news-list { display: flex; flex-direction: column; gap: 16px; }
.news-item {
  display: flex;
  gap: 18px;
  padding: 16px;
  background: #f7f8fa;
  border-radius: 8px;
  transition: all 0.25s;
}
.news-item:hover { background: #eef4ff; }
.news-cover {
  flex: 0 0 200px;
  height: 120px;
  border-radius: 6px;
  overflow: hidden;
  background: #eef0f3;
}
.news-cover img { width: 100%; height: 100%; object-fit: cover; }
.news-body { flex: 1; min-width: 0; }
.news-title {
  margin: 4px 0 8px;
  font-size: 18px;
  color: #1f2d3d;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.news-summary {
  margin: 0 0 14px;
  font-size: 14px;
  color: #606266;
  line-height: 1.7;
  /* 摘要限制两行 */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.news-meta { color: #909399; font-size: 13px; }
.news-meta i { margin-right: 4px; }
</style>
